/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { IOrder, IOrderItem } from '@/interfaces/order';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import Title from '@/components/title';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  notification,
  Row,
  Select,
  Table,
  TimePicker,
} from 'antd';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import locale from 'antd/es/date-picker/locale/th_TH';
import CustomerSelector from '@/components/selector/customer-selector';
import ShippingAddressSelector from '@/components/selector/shipping-address-selector';
import { getShippingTypeOptions } from '@/helpers/shippingType';
import UserSelector from '@/components/selector/user-selector';
import dayjs, { Dayjs } from 'dayjs';
import { ICustomer } from '@/interfaces/customer';
import { round } from 'lodash';
import StockSelectorModal from '@/components/stock/stock-selector-modal';
import { PlusIcon } from '@heroicons/react/24/solid';
import { IStock } from '@/interfaces/stock';
import { useMutation } from '@tanstack/react-query';
import { editOrder } from '@/adapters/order';
import { twMerge } from 'tailwind-merge';
import { mediaView } from '@/utils/mediaUtils';
import { getShippingFullAddress } from '@/utils/shippingAddressUtils';
import ConfirmApprove from '@/components/modal/confirm-approve';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { IDataModal } from '@/interfaces/dataModal';
import { isManager } from '@/utils/permissionUtils';
import { EOrderType } from '@/enums/EOrder';

interface IProps {
  data: IOrder;
  onEdit: () => void;
}

const _modal: IDataModal<Partial<IOrder>> = {
  open: false,
};

const OrderEditInfo = ({ data, onEdit }: IProps) => {
  const perManager = isManager();
  const [mainForm] = Form.useForm<Partial<IOrder>>();
  const [form] = Form.useForm();
  const [dataItem, setDataItem] = useState<Partial<IOrderItem>[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const [discount, setDiscount] = useState(0);
  const [discountAlter, setDiscountAlter] = useState(0);
  const [vatRate, setVatRate] = useState(7);
  const [isAddStock, setIsAddStock] = useState(false);
  const [completedOrder, setCompletedOrder] =
    useState<IDataModal<Partial<IOrder>>>(_modal);

  const isEditing = (record: Partial<IOrderItem>) => record.key === editingKey;
  const onCustomerChange = (customerInfo: ICustomer) => {
    mainForm.setFieldsValue({
      customerId: customerInfo.id,
      shippingAddressId: undefined,
      creditTerm: customerInfo.creditTerm,
      dueDate: dayjs(_orderDate).add(customerInfo.creditTerm, 'day'),
      salesId: customerInfo.salesId,
    });
  };

  const _orderDate = Form.useWatch('orderDate', mainForm);
  const _customerId = Form.useWatch('customerId', mainForm);
  const _shippingAddressId = Form.useWatch('shippingAddressId', mainForm);
  const _salesId = Form.useWatch('salesId', mainForm);
  const _creditTerm = Form.useWatch('creditTerm', mainForm);
  const _shippingAddressText = Form.useWatch('shippingAddressText', mainForm);
  const _soNo = Form.useWatch('soNo', mainForm);
  const _poNo = Form.useWatch('poNo', mainForm);
  const _ivNo = Form.useWatch('ivNo', mainForm);
  const _currency = Form.useWatch('currency', mainForm);

  useEffect(() => {
    if (data) {
      mainForm.setFieldsValue({
        ...data,
        orderDate: dayjs(data?.orderDate),
        dueDate: dayjs(data?.dueDate),
        dueDateTime:
          (data?.dueDateTime && dayjs(data?.dueDateTime, 'HH:mm')) || undefined,
        poDate: data?.poDate && dayjs(data?.poDate),
        eta: data?.eta && dayjs(data?.eta),
      });

      const initialOrderItems =
        data?.orderItems?.map(o => {
          o.key = uuidv4();
          return o;
        }) || [];

      setDataItem(initialOrderItems);
      setVatRate(data?.vatRate);
      setDiscount(Number(data?.discountAmount || 0));
      setDiscountAlter(Number(data?.discountAmountAlter || 0));
    }
  }, [data]);

  const edit = (record: Partial<IOrderItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<IOrderItem>;

      const newData = [...dataItem];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setDataItem(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setDataItem(newData);
        setEditingKey('');
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  interface EditableCellProps extends React.HTMLAttributes<any> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'number' | 'text' | 'checkbox';
    record: Partial<IOrderItem>;
    index: number;
  }

  const onDeleteOrderItem = (item: IOrderItem) => {
    setDataItem(prev => prev.filter(p => p.key !== item.key));
  };

  const onRowClick = (record: Partial<IOrderItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'checkbox' ? (
        <Checkbox onBlur={() => save(record.key || '')}>{title}</Checkbox>
      ) : inputType === 'number' ? (
        <InputNumber
          className="w-full"
          type="number"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          decimalSeparator={'.'}
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input onBlur={() => save(record.key || '')} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            valuePropName={inputType === 'checkbox' ? 'checked' : undefined}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'qtNo',
      width: 140,
      className: 'font-bold',
      align: 'center',
      render: (_: number, rec: IOrderItem) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-center">
          <div
            className={twMerge(
              !rec.qtNo && 'text-gray-400',
              rec.qtId && 'text-blue-600 underline',
            )}
            onClick={() => (rec.qtId ? mediaView({ id: rec.qtId }) : false)}
          >
            {_ ?? ''}
          </div>
        </div>
      ),
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity / rec.conversion, 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 110,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      editable: editingIndex === 'quantity',
      dataIndex: 'quantity',
      className: 'font-bold text-green-700',
      align: 'right',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'quantity')}
        >
          {formatNumber(_, 2)}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: 'หน่วยขาย',
      width: 80,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
    },
    {
      title: 'FOC',
      width: 60,
      dataIndex: 'isFoc',
      className: 'font-bold',
      editable: editingIndex === 'isFoc',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-between"
          onClick={() => onRowClick(rec, 'isFoc')}
        >
          {(_ && 'FOC') || '-'}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: 'ราคา (THB)',
      editable: editingIndex === 'unitPrice',
      width: 100,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-blue-700',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'unitPrice')}
        >
          {formatNumber(_, 2)}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },

    {
      title: 'รวมเงิน (THB)',
      width: 120,
      dataIndex: 'totalPrice',
      align: 'right',
      className: 'font-bold',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity * (rec.unitPrice || 0), 2), 2),
    },
    {
      title: `FX PRICE (${_currency || '-'})`,
      editable: editingIndex === 'alterUnitPrice',
      width: 120,
      dataIndex: 'alterUnitPrice',
      align: 'right',
      className: 'font-bold text-blue-700',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'alterUnitPrice')}
        >
          {formatNumber(_, 2)}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: `รวมเงิน (${_currency || '-'})`,
      width: 120,
      dataIndex: 'totalPrice',
      align: 'right',
      className: 'font-bold',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity * (rec.alterUnitPrice || 0), 2), 2),
    },
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IOrderItem) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => onDeleteOrderItem(rec)}
        />
      ),
    },
  ];

  const mergedColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IOrderItem) => ({
        record,
        inputType:
          col.dataIndex === editingIndex
            ? editingIndex === 'isFoc'
              ? 'checkbox'
              : 'number'
            : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const onAddOrderItem = (items: IStock[]) => {
    const orderItems: Partial<IOrderItem>[] = items.map(i => {
      const orderItem: Partial<IOrderItem> = {
        ...i,
        key: uuidv4(),
        id: 0,
        stockId: i.id,
        quantity: i.quantity,
        remain: i.quantity,
        lotNumber: i.code,
        orderId: data.id,
        isFoc: false,
        alterCurreny: '',
        alterTotalPrice: 0,
        alterUnitPrice: 0,
      };
      return orderItem;
    });
    setDataItem([...dataItem, ...orderItems]);
    // setIsAddStock(false);
  };

  const mutation = useMutation({
    mutationFn: async (updateData: Partial<IOrder>) =>
      await editOrder(updateData),
    mutationKey: ['update-order'],
    onSuccess: updateData => {
      if (updateData?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'บันทึกออเดอร์สำเร็จ',
        });
        onEdit();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    mainForm
      .validateFields()
      .then(async (values: Partial<IOrder>) => {
        if (!dataItem.length) {
          message.warning('กรุณาเลือกรายการ');
          return;
        }

        values.orderItems = dataItem.map(d => {
          const orderItem: Partial<IOrderItem> = {
            id: 0,
            stockId: d.stockId,
            quantity: d.quantity,
            unitPrice: d.unitPrice,
            conversion: d.conversion,
            baseUnitId: d.baseUnitId,
            packageUnitId: d.packageUnitId,
            orderId: data.id,
            qtId: d.qtId,
            qtNo: d.qtNo,
            alterCurreny: _currency,
            alterUnitPrice: d.alterUnitPrice,
            isFoc: d.isFoc,
          };
          return orderItem;
        });
        values.id = data.id;
        values.orderType = EOrderType.EXPORT;
        values.orderDate = dayjs(values.orderDate).toDate();
        values.dueDate = dayjs(values.dueDate).toDate();
        values.vatRate = vatRate;
        values.discountAmount = discount;
        values.dueDateTime =
          (values.dueDateTime && dayjs(values.dueDateTime).format('HH:mm')) ||
          undefined;
        values.discountAmountAlter = discountAlter;
        values.eta = values.eta && dayjs(values.eta).toDate();
        values.poDate = values.poDate && dayjs(values.poDate).toDate();
        setCompletedOrder({
          ...completedOrder,
          open: true,
          item: values,
        });
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  let totalPrice = 0;
  let summaryPrice = 0;
  let totalAlterPrice = 0;
  let summaryAlterPrice = 0;
  let totalAlterPriceFOC = 0;
  let totalAlterPriceFOCAlter = 0;

  dataItem?.forEach(({ quantity, unitPrice }) => {
    totalPrice += Number(round((quantity || 1) * (unitPrice || 0), 2));
  });
  dataItem
    ?.filter(c => !!c.isFoc)
    ?.forEach(({ quantity, unitPrice }) => {
      totalAlterPriceFOC += Number(
        round((quantity || 1) * (unitPrice || 0), 2),
      );
    });
  dataItem?.forEach(({ quantity, alterUnitPrice }) => {
    totalAlterPrice += Number(
      round((quantity || 1) * (alterUnitPrice || 0), 2),
    );
  });
  dataItem
    ?.filter(c => !!c.isFoc)
    .forEach(({ quantity, alterUnitPrice }) => {
      totalAlterPriceFOCAlter += Number(
        round((quantity || 1) * (alterUnitPrice || 0), 2),
      );
    });
  const vatPrice = round(
    ((totalPrice - (discount + (totalAlterPriceFOC || 0))) * vatRate) / 100,
    2,
  );
  const vatAlterPrice = round(
    ((totalAlterPrice - (discountAlter + (totalAlterPriceFOCAlter || 0))) *
      vatRate) /
      100,
    2,
  );
  summaryPrice = totalPrice - (discount + (totalAlterPriceFOC || 0)) + vatPrice;
  summaryAlterPrice =
    totalAlterPrice -
    (discountAlter + (totalAlterPriceFOCAlter || 0)) +
    vatAlterPrice;

  return (
    <>
      <Title
        customClassName="text-lg text-blue-700"
        customContainerClassName="py-0"
        title={`รายละเอียด`}
      />
      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          initialValues={{
            orderDate: dayjs(),
          }}
          autoComplete="off"
        >
          <Form.Item<IOrder> name="shippingAddressText" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IOrder> name="shipToRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={24}>
            <Col md={24} lg={16} xxl={18}>
              <Row gutter={12}>
                <Col xs={24} md={24} lg={12} xxl={8}>
                  <Form.Item
                    label="ลูกค้า"
                    name="customerId"
                    rules={[{ required: true, message: 'เลือกลูกค้า' }]}
                  >
                    <CustomerSelector
                      handleSelectData={onCustomerChange}
                      customer={data?.customer}
                      readonly={data?.isConfirm}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={8} xxl={8}>
                  <Form.Item<IOrder>
                    label="ที่จัดส่ง"
                    name="shippingAddressId"
                    rules={[{ required: true, message: 'เลือกที่จัดส่ง' }]}
                  >
                    <ShippingAddressSelector
                      value={_shippingAddressId}
                      customerId={_customerId}
                      handleSelectData={(id, item) => {
                        mainForm.setFieldsValue({
                          shippingAddressId: id,
                          shipToRemark: item?.remark,
                          shippingAddressText: getShippingFullAddress(item),
                          dueDateTime:
                            (item?.dueDateTime &&
                              dayjs(item?.dueDateTime, 'HH:mm')) ||
                            undefined,
                        });
                      }}
                      readonly={data?.isConfirm}
                    />
                    {_shippingAddressText && (
                      <div className="text-blue-700">
                        {_shippingAddressText}
                      </div>
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={4} xxl={4}>
                  <Form.Item<IOrder>
                    label="จัดส่งโดย"
                    name="transportation"
                    rules={[{ required: true, message: 'เลือกจัดส่งโดย' }]}
                  >
                    <Select
                      allowClear
                      className="md:min-w-[150px]"
                      size="large"
                      placeholder="จัดส่งโดย"
                      options={getShippingTypeOptions()}
                      disabled={data?.isConfirm}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder> label="เลขที่ออเดอร์" name="code">
                    <Input
                      size="large"
                      placeholder="เลขที่ใบแจ้งหนี้"
                      readOnly
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder>
                    label="เลขที่ใบสั่งสินค้า (CO)"
                    name="soNo"
                  >
                    <Input size="large" placeholder="เลขที่ใบสั่งสินค้า" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder>
                    label="เลขที่ใบแจ้งหนี้ (IV)"
                    name="ivNo"
                    // rules={[
                    //   {
                    //     pattern: /^[0-9]+$/,
                    //     message: 'ระบุเฉพาะตัวเลขเท่านั้น',
                    //   },
                    // ]}
                  >
                    <Input size="large" placeholder="เลขที่ใบแจ้งหนี้" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder> label="เลขที่ใบสั่งซื้อ (PO)" name="poNo">
                    <Input size="large" placeholder="เลขที่ใบสั่งซื้อ" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={5} xxl={4}>
                  <Form.Item<Partial<IOrder>>
                    label="วันที่สั่งซื้อ (PO Date)"
                    name="poDate"
                    rules={[
                      { required: true, message: 'กรุณาเลือกวันที่สั่งซื้อ' },
                    ]}
                  >
                    <DatePicker
                      size="large"
                      className="w-full"
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder> label="ส่งสินค้าภายใน" name="dueDateTime">
                    <TimePicker
                      size="large"
                      format="HH:mm"
                      value={dayjs(form.getFieldValue('dueDateTime'), 'HH:mm')}
                      minuteStep={30}
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col md={24} lg={8} xxl={6}>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="พนักงานขาย"
                    name="salesId"
                    rules={[{ required: true, message: 'เลือกพนักงานขาย' }]}
                  >
                    <UserSelector
                      value={_salesId}
                      handleSelectData={id =>
                        mainForm.setFieldsValue({
                          salesId: id,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="วันที่สั่งซื้อ"
                    name="orderDate"
                    rules={[
                      { required: true, message: 'กรุณาเลือกวันที่สั่งซื้อ' },
                    ]}
                  >
                    <DatePicker
                      disabled={!!data?.isConfirm && !perManager}
                      size="large"
                      className="w-full"
                      locale={locale}
                      onChange={d => {
                        mainForm.setFieldsValue({
                          dueDate: dayjs(d).add(Number(_creditTerm), 'day'),
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="กำหนดชำระ (วัน)"
                    name="creditTerm"
                    rules={[{ required: true, message: 'กำหนดชำระ (วัน)' }]}
                  >
                    <InputNumber
                      size="large"
                      className="w-full"
                      placeholder="กำหนดชำระ (วัน)"
                      onChange={v => {
                        mainForm.setFieldsValue({
                          dueDate: dayjs(_orderDate).add(v as number, 'day'),
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="ครบกำหนดชำระ"
                    name="dueDate"
                    rules={[
                      { required: true, message: 'กรุณาเลือกครบกำหนดชำระ' },
                    ]}
                  >
                    <DatePicker
                      size="large"
                      className="w-full"
                      locale={locale}
                      allowClear={false}
                      onChange={d => {
                        const creditTerm = d.diff(dayjs(_orderDate), 'day');
                        mainForm.setFieldsValue({
                          creditTerm: creditTerm,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={12} className="flex justify-end">
            <Col xs={24} md={3}>
              <Form.Item<IOrder>
                label="Currency"
                name="currency"
                rules={[{ required: true, message: 'ระบุ Currency' }]}
              >
                <Input size="large" placeholder="THB, USD" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`รายการ`}
      />
      <div className="flex w-full mt-5">
        <Form form={form} component={false}>
          <Table
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            locale={{
              emptyText: 'เพิ่มรายการใหม่',
            }}
            size="middle"
            bordered
            dataSource={dataItem}
            columns={mergedColumns}
            className="w-full"
            rowClassName="editable-row"
            pagination={false}
            scroll={{ x: 1600 }}
            summary={() => {
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={10}
                      className="font-bold items-end"
                      align="right"
                    >
                      รวมเงิน
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="right"
                    >
                      {formatNumber(totalPrice, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="left"
                    >
                      THB
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="right"
                    >
                      {formatNumber(totalAlterPrice, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="left"
                    >
                      {_currency}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={10}
                      className="font-bold items-end"
                      align="right"
                    >
                      ส่วนลดจาก FOC
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="right"
                    >
                      {formatNumber(totalAlterPriceFOC, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="left"
                    >
                      THB
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="right"
                    >
                      {formatNumber(totalAlterPriceFOCAlter, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="left"
                    >
                      {_currency}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={10}
                      className="font-bold items-end"
                      align="right"
                    >
                      ส่วนลด
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="right"
                    >
                      <InputNumber
                        className="w-full "
                        value={discount}
                        style={{ textAlignLast: 'right' }}
                        controls={false}
                        decimalSeparator="."
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value =>
                          value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                        }
                        precision={2}
                        onChange={value => {
                          setDiscount(value || 0);
                        }}
                        min={0}
                      />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="left"
                    >
                      THB
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="right"
                    >
                      <InputNumber
                        className="w-full"
                        value={discountAlter}
                        style={{ textAlignLast: 'right' }}
                        controls={false}
                        decimalSeparator="."
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value =>
                          value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                        }
                        precision={2}
                        onChange={value => {
                          setDiscountAlter(value || 0);
                        }}
                        min={0}
                      />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="left"
                    >
                      {_currency}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={10}
                      className="font-bold items-end"
                      align="right"
                    >
                      ยอดรวมหลังหักส่วนลด
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="right"
                    >
                      {formatNumber(
                        totalPrice - (discount + (totalAlterPriceFOC || 0)),
                        2,
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="left"
                    >
                      THB
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="right"
                    >
                      {formatNumber(
                        totalAlterPrice -
                          (discountAlter + (totalAlterPriceFOCAlter || 0)),
                        2,
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="left"
                    >
                      {_currency}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={10}
                      className="font-bold items-end"
                      align="right"
                    >
                      VAT{' '}
                      <InputNumber
                        value={vatRate}
                        onChange={value => {
                          setVatRate(value || 0);
                        }}
                        min={0}
                        suffix="%"
                      />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="right"
                    >
                      {formatNumber(vatPrice || 0, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="left"
                    >
                      THB
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="right"
                    >
                      {formatNumber(vatAlterPrice || 0, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="left"
                    >
                      {_currency}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={10}
                      className="font-bold items-end"
                      align="right"
                    >
                      ยอดสุทธิ
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="right"
                    >
                      {formatNumber(summaryPrice || 0, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-red-100"
                      align="left"
                    >
                      THB
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="right"
                    >
                      {formatNumber(summaryAlterPrice || 0, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end bg-blue-100"
                      align="left"
                    >
                      {_currency}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Form>
      </div>
      <div className="flex justify-end mt-3">
        {!data?.isConfirm && (
          <Button
            type="default"
            className="text-blue-700 font-bold"
            onClick={() => setIsAddStock(true)}
          >
            <PlusIcon className="w-3 h-3" /> เพิ่มรายการ
          </Button>
        )}
        <StockSelectorModal
          handleCancel={() => setIsAddStock(false)}
          handleOk={onAddOrderItem}
          open={isAddStock}
          // exists={stockIds}
          customerId={Number(_customerId)}
        />
      </div>
      <div className="flex w-full">
        <Form form={mainForm} layout="vertical" className="w-full">
          <Row gutter={12}>
            <Col xs={24} md={4}>
              <Form.Item<IOrder> label="Export Type" name="exportType">
                <Input size="large" placeholder="Animal Feed Additives" />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<IOrder>
                label="Country of Origin"
                name="countryOfOrgin"
              >
                <Input size="large" placeholder="Country of Origin" />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<IOrder> label="Incoterm" name="incoTerm">
                <Input size="large" placeholder="Incoterm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<IOrder> label="ETA" name="eta">
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  placeholder="ETA DATE"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<IOrder> label="Payment term" name="paymentTerm">
                <Input size="large" placeholder="Normal TT" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={{ span: 4 }}>
              <Form.Item<IOrder> label="Gross Weight" name="grossWeight">
                <InputNumber
                  size="large"
                  placeholder="Gross Weight"
                  className="w-full"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<IOrder> label="Net Weight" name="netWeight">
                <InputNumber
                  size="large"
                  placeholder="Net Weight"
                  className="w-full"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input.TextArea rows={3} placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<IOrder> label="หมายเหตุคลัง" name="whRemark">
                <Input.TextArea rows={3} placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<IOrder> label="หมายเหตุขนส่ง" name="logisticRemark">
                <Input.TextArea rows={3} placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<IOrder>
                label="หมายเหตุขนส่ง (ใบส่งของ)"
                name="shipToRemark"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="หมายเหตุขนส่ง (ใบส่งของ)"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<IOrder> label="หมายเหตุวางบิล" name="billingRemark">
                <Input.TextArea rows={3} placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="flex justify-end mt-5 gap-2">
        <Button
          type="default"
          htmlType="submit"
          size="large"
          onClick={() => onEdit()}
        >
          ยกเลิก
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          className=" font-bold min-w-[150px]"
          loading={mutation.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>
      </div>
      <ConfirmApprove
        content={`ยืนยันการอัพเดทออเดอร์ ใช่หรือไม่`}
        handleCancel={() => {
          setCompletedOrder({ ..._modal });
        }}
        handleOk={async () => {
          await mutation.mutateAsync(completedOrder?.item as Partial<IOrder>);
        }}
        open={completedOrder?.open}
        title={`ยืนยันการจัดสินค้า`}
        showInfo
        info={
          <div className="flex flex-col gap-2">
            <div className="flex flex-col justify-end mt-2 gap-1">
              <DetailItem
                customLabelClass="md:min-w-[80px]"
                label="ลูกค้า"
                detail={data?.customerName}
              />
              <DetailItem
                customLabelClass="md:min-w-[80px]"
                label="วันที่สั่งซื้อ"
                detail={formatDate(_orderDate as Dayjs)}
              />
              <DetailItem
                customLabelClass="md:min-w-[80px]"
                label="CO"
                detail={_soNo}
              />
              <DetailItem
                customLabelClass="md:min-w-[80px]"
                label="IV"
                detail={_ivNo}
              />
              <DetailItem
                customLabelClass="md:min-w-[80px]"
                label="PO"
                detail={_poNo}
              />
              <DetailItem
                customLabelClass="md:min-w-[80px]"
                label="ออเดอร์"
                detail={`${dataItem?.length} รายการ`}
              />
              <div className="flex flex-col border border-red-400 rounded-lg p-2 text-bold w-full bg-red-50">
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px]  text-sm"
                  label="รวมเงิน"
                  detail={`${formatNumber(totalPrice, 2)} THB`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label="ส่วนลด FOC"
                  detail={`${formatNumber(Number(totalAlterPriceFOC), 2)} THB`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label="ส่วนลด"
                  detail={`${formatNumber(Number(discount), 2)} THB`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label="รวมเงินหลังหักส่วนลด"
                  detail={`${formatNumber(
                    Number(
                      totalPrice -
                        Number((discount || 0) + (totalAlterPriceFOC || 0)),
                    ),
                    2,
                  )} THB`}
                  customClass="flex justify-end w-full text-black font-bold text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label={`VAT ${vatRate} %`}
                  detail={`${formatNumber(vatPrice, 2)} THB`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px]   text-sm"
                  label="ยอดสุทธิ"
                  detail={`${formatNumber(summaryPrice, 2)} THB`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col justify-end mt-2 gap-2">
              <div className="flex flex-col border border-blue-400 rounded-lg p-2 text-bold w-full bg-blue-50">
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px]  text-sm"
                  label="รวมเงิน"
                  detail={`${formatNumber(totalAlterPrice, 2)} ${_currency}`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label="ส่วนลด FOC"
                  detail={`${formatNumber(Number(totalAlterPriceFOCAlter), 2)} ${_currency}`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label="ส่วนลด"
                  detail={`${formatNumber(Number(discountAlter), 2)} ${_currency}`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label="รวมเงินหลังหักส่วนลด"
                  detail={`${formatNumber(
                    Number(
                      totalAlterPrice -
                        Number(
                          (discountAlter || 0) + (totalAlterPriceFOCAlter || 0),
                        ),
                    ),
                    2,
                  )} ${_currency}`}
                  customClass="flex justify-end w-full text-black font-bold text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px] text-sm"
                  label={`VAT ${vatRate} %`}
                  detail={`${formatNumber(vatPrice, 2)} ${_currency}`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
                <DetailItem
                  customMainClass="flex-row"
                  customLabelClass="flex justify-end min-w-[140px]   text-sm"
                  label="ยอดสุทธิ"
                  detail={`${formatNumber(summaryAlterPrice, 2)} ${_currency}`}
                  customClass="flex justify-end w-full text-black font-bold  text-sm"
                />
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default OrderEditInfo;
